import { MemoryMessageToSend } from "@/components/chat/chat-conversation";
import type { CompanyIntegrationsSyncState } from "@/components/integrations/company/company-integrations-sync";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useChatStream } from "@/contexts/chat-stream-context";
import {
  hasRunSnapshot,
  toCompanySectionsState,
  type CompanySectionsState,
} from "@/contexts/company-sections-context";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useTeams } from "@/contexts/teams-provider";
import { useChatOpenListener } from "@/hooks/useChatActions";
import { useCompanyPage, useCompanyPageErrorToast } from "@/hooks/useCompanyPage";
import { useLlmSelection } from "@/hooks/useLlmSelection";
import { useTranslation } from "@/hooks/useTranslation";
import { setWindowTitle } from "@/lib/window-title";
import { useApiStream, type SSEError } from "@/hooks/useApi";
import { usePrefetchCompanyTabs } from "@/hooks/usePrefetchCompanyTabs";
import { companyPageProgress, companyPageStore } from "@/stores/company-page-store";
import type { CompanyDTO, UserDTO } from "@shaarp/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePanelRef } from "react-resizable-panels";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export type ChatMode = "modal" | "sidepanel" | null;

export type CompanyPageState = {
  // i18n
  t: ReturnType<typeof useTranslation>["t"];

  // Data
  company: CompanyDTO | null;
  /** Share of the sections settled, 0 to 100; 100 once the run is over. */
  progress: number;
  /** The run is over: every section settled, or the stream ended early. */
  done: boolean;
  /** Each section's status, for the cards gated on their own sections. */
  sections: CompanySectionsState;
  /** The run ended on the team's spent enrichment budget: the stored row only. */
  budgetSpent: boolean;
  /** True until the first company arrives or the run ends without one. */
  isLoadingReady: boolean;
  error: SSEError | null;

  // Navigation
  activeTab: string;
  defaultTab: string;

  // Chat
  chatMode: ChatMode;
  activeConversationId: string | null;
  inputExpanded: boolean;
  promptInput: string;
  initialMessage: MemoryMessageToSend | null;
  promptInputRef: React.RefObject<HTMLInputElement | null>;

  // Dialogs
  dialogEdit: boolean;
  dialogJson: boolean;

  // Shared action state (sticky header ↔ main header sync)
  sharedSavedNote: string;
  sharedSelectedTag: string | null;
  sharedAssignedUser: UserDTO | null;
  sharedIntegrationSync: CompanyIntegrationsSyncState;

  // Refs
  refResizableChat: ReturnType<typeof usePanelRef>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  headerRef: React.RefObject<HTMLDivElement | null>;

  // Derived
  isHeaderVisible: boolean;
  showActions: boolean;

  // Context values
  isAdminMode: boolean;
  isStreamInProgress: boolean;
  /** Whether the team has connected at least one CRM, gating the send controls. */
  hasConnectedCrm: boolean;

  // Setters exposed for JSX event handlers
  setChatMode: (mode: ChatMode) => void;
  setInputExpanded: (expanded: boolean) => void;
  setPromptInput: (input: string) => void;
  setInitialMessage: (message: MemoryMessageToSend | null) => void;
  setDialogEdit: (open: boolean) => void;
  setDialogJson: (open: boolean) => void;
  setSharedSavedNote: (note: string) => void;
  setSharedSelectedTag: (tag: string | null) => void;
  setSharedAssignedUser: (user: UserDTO | null) => void;
  setSharedIntegrationSync: (state: CompanyIntegrationsSyncState) => void;

  // Handlers
  /** Restart the company stream, aborting the running one. */
  fetchCompanyData: () => void;
  handleTabChange: (value: string) => void;
  /** The user reached for a tab's trigger: prefetch what it reads. */
  handleTabIntent: (value: string) => void;
  /** The pointer left a tab's trigger: prefetch nothing more for it. */
  handleTabIntentEnd: (value: string) => void;
  handlePromptSubmit: () => void;
  handleConversationChange: (id: string | undefined) => void;
  handleMoveToSidePanel: () => void;
};

/**
 * Owns all state, refs, effects, and event handlers for the CompanyPage.
 * CompanyPage itself becomes a thin rendering shell that destructures this hook.
 */
export function useCompanyPageState(): CompanyPageState {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdminMode } = useAdminMode();
  const { isStreamInProgress } = useChatStream();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const { teamCompanySelected } = useTeams();

  const refResizableChat = usePanelRef();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const { companyId = "" } = useParams();

  const defaultTab = "insights";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  const page = useCompanyPage(companyId);
  const { company, done, error, budgetSpent } = page;
  const progress = companyPageProgress(page);
  const sections = useMemo(
    () => toCompanySectionsState({ sections: page.sections, done, budgetSpent }),
    [page.sections, done, budgetSpent],
  );
  const isLoadingReady = company === null && !done;
  useCompanyPageErrorToast(error, company !== null);
  // Until this run's snapshot arrives nothing may be prefetched (a revisit
  // keeps the company).
  const hasSnapshot = hasRunSnapshot(sections);
  const {
    onTabIntent: handleTabIntent,
    onTabIntentEnd: handleTabIntentEnd,
  } = usePrefetchCompanyTabs(
    companyId,
    hasSnapshot,
    page.sections,
  );

  const { get, teamId } = useApiStream();
  const fetchCompanyData = useCallback(() => {
    void companyPageStore.refresh(teamId, companyId, get);
  }, [teamId, companyId, get]);

  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(true);
  const [chatMode, setChatMode] = useState<ChatMode>(null);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [inputExpanded, setInputExpanded] = useState<boolean>(false);
  const [promptInput, setPromptInput] = useState<string>("");
  const [initialMessage, setInitialMessage] =
    useState<MemoryMessageToSend | null>(null);
  const promptInputRef = useRef<HTMLInputElement>(null);
  const [dialogEdit, setDialogEdit] = useState(false);
  const [dialogJson, setDialogJson] = useState(false);

  // Shared state for action buttons so sticky header stays in sync
  const [sharedSavedNote, setSharedSavedNote] = useState<string>("");
  const [sharedSelectedTag, setSharedSelectedTag] = useState<string | null>(
    null,
  );
  const [sharedAssignedUser, setSharedAssignedUser] = useState<UserDTO | null>(
    null,
  );
  const [sharedIntegrationSync, setSharedIntegrationSync] =
    useState<CompanyIntegrationsSyncState>({
      isSynced: false,
      crmCompany: null,
      possiblesMatchCrmCompanies: [],
    });

  const { llmSelection } = useLlmSelection();

  // Listen for chat open events from CTA buttons
  useChatOpenListener(() => {
    setChatMode("sidepanel");
  });

  // Focus input when expanded
  useEffect(() => {
    if (inputExpanded) {
      // Small delay to let the transition start before focusing
      const timeout = setTimeout(() => promptInputRef.current?.focus(), 50);
      return () => clearTimeout(timeout);
    }
  }, [inputExpanded]);

  // Close expanded input when chat opens (modal or sidepanel)
  useEffect(() => {
    if (chatMode !== null) {
      setInputExpanded(false);
      setPromptInput("");
    }
  }, [chatMode]);

  const pendingPromptRef = useRef<string | null>(null);

  const handlePromptSubmit = useCallback(() => {
    const text = promptInput.trim();
    if (!text) return;

    pendingPromptRef.current = text;
    setInitialMessage({
      llmId: llmSelection ?? "auto",
      text,
      filesAttached: [],
    });
    setChatMode("modal");
  }, [promptInput, llmSelection]);

  /** Register the conversation once the modal's CompanyChat reports its id. */
  const handleConversationChange = useCallback((id: string | undefined) => {
    setActiveConversationId(id ?? null);
    if (id && pendingPromptRef.current) {
      pendingPromptRef.current = null;
    }
  }, []);

  const handleMoveToSidePanel = useCallback(() => {
    setChatMode("sidepanel");
  }, []);

  useEffect(() => {
    setActiveConversationId(null);
    setChatMode(null);
  }, [companyId]);

  useEffect(() => {
    setWindowTitle(company?.name || t("company.defaults.companyTitle"));
  }, [company, t]);

  // Sync shared action state when company data loads. On the content, not the
  // id: a reopened page first paints the company kept from its last visit,
  // then its fresh snapshot, with the same id and the team's latest note.
  const loadedCompanyId = company?.id;
  const noteContent = company?.note?.content;
  const assignedTag = company?.assignation?.tag;
  useEffect(() => {
    if (loadedCompanyId) {
      setSharedSavedNote(noteContent || "");
      setSharedSelectedTag(assignedTag || null);
    }
  }, [loadedCompanyId, noteContent, assignedTag]);

  useEffect(() => {
    const paths = location.pathname.replace("/company/", "").split("/");
    if (paths.length <= 1) {
      navigate(defaultTab, { replace: true });
      return;
    }

    setActiveTab(paths.pop() ?? defaultTab);
  }, [companyId, location.pathname, navigate]);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    navigate(value);
    scrollContainerRef.current?.scrollTo({ top: 0 });
    // A tab reached without pointer or focus (a keyboard shortcut) still reads.
    handleTabIntent(value);
  }, [navigate, handleTabIntent]);

  useEffect(() => {
    const panels = document.querySelectorAll<HTMLElement>("[data-panel]");

    // Apply transition before resizing so the browser animates the change
    panels.forEach((panel) => {
      panel.style.transition = "flex-grow 0.3s ease-in-out";
    });

    // Use rAF to ensure the transition property is applied before the resize
    requestAnimationFrame(() => {
      if (chatMode === "sidepanel") {
        refResizableChat.current?.resize("30%");
      } else {
        // The panel enforces a minimum width, so a plain resize("0px")
        // would clamp up to that minimum instead of hiding it — collapse to the
        // 0px collapsedSize to fully close it.
        refResizableChat.current?.collapse();
      }
    });

    const cleanup = setTimeout(() => {
      panels.forEach((panel) => {
        panel.style.transition = "";
      });
    }, 350);

    return () => clearTimeout(cleanup);
  }, [chatMode, refResizableChat]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const headerElement = headerRef.current;
    if (!scrollContainer || !headerElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsHeaderVisible(entry.isIntersecting),
      { root: scrollContainer, threshold: 0 },
    );
    observer.observe(headerElement);
    return () => observer.disconnect();
  }, [isLoadingReady]);

  const showActions =
    !teamCompanySelected || (company ? teamCompanySelected.id !== company.id : true);

  return {
    // Data
    company,
    progress,
    done,
    sections,
    budgetSpent,
    isLoadingReady,
    error,

    // Navigation
    activeTab,
    defaultTab,

    // Chat
    chatMode,
    activeConversationId,
    inputExpanded,
    promptInput,
    initialMessage,
    promptInputRef,

    // Dialogs
    dialogEdit,
    dialogJson,

    // Shared action state
    sharedSavedNote,
    sharedSelectedTag,
    sharedAssignedUser,
    sharedIntegrationSync,

    // Refs
    refResizableChat,
    scrollContainerRef,
    headerRef,

    // Derived
    isHeaderVisible,
    showActions,

    // Context values
    isAdminMode,
    isStreamInProgress,
    hasConnectedCrm: connectedCrmIntegrations.length > 0,

    // Setters
    setChatMode,
    setInputExpanded,
    setPromptInput,
    setInitialMessage,
    setDialogEdit,
    setDialogJson,
    setSharedSavedNote,
    setSharedSelectedTag,
    setSharedAssignedUser,
    setSharedIntegrationSync,

    // i18n
    t,

    // Handlers
    fetchCompanyData,
    handleTabChange,
    handleTabIntent,
    handleTabIntentEnd,
    handlePromptSubmit,
    handleConversationChange,
    handleMoveToSidePanel,
  };
}
